import { SearchCriteria, toSearchParams } from './criteria';

// The two advanced-search routes and how they are built. The criteria
// always go in the query string (see criteria.ts); the only thing passed through
// `location.state` is `from`, the route the modal returns to when closing.

export const SEARCH_PATH = '/search';
export const SEARCH_RESULTS_PATH = '/search/results';

// The modal with the form seeded from what was typed in the Home's
// search box ("Que cherchez-vous ?").
export const searchDialogPath = (keywordsText = ''): string => {
  const text = keywordsText.trim();
  if (!text) return SEARCH_PATH;
  return `${SEARCH_PATH}?${new URLSearchParams({ q: text }).toString()}`;
};

export const searchDialogPathFor = (criteria: SearchCriteria): string => {
  const query = toSearchParams(criteria).toString();
  return query ? `${SEARCH_PATH}?${query}` : SEARCH_PATH;
};

const DEFAULT_LIMIT = 25;

// The Résultats query string: the criteria and the pagination. The first
// page with the default size is not written, so that the "clean" URL and
// the one just arrived from the modal are the same.
export const searchResultsParams = (
  criteria: SearchCriteria,
  pagination?: { page: number; limit: number },
): URLSearchParams => {
  const params = toSearchParams(criteria);
  if (pagination && pagination.page > 1) params.set('page', String(pagination.page));
  if (pagination && pagination.limit !== DEFAULT_LIMIT) {
    params.set('limit', String(pagination.limit));
  }
  return params;
};

export const searchResultsPathFor = (
  criteria: SearchCriteria,
  pagination?: { page: number; limit: number },
): string => {
  const query = searchResultsParams(criteria, pagination).toString();
  return query ? `${SEARCH_RESULTS_PATH}?${query}` : SEARCH_RESULTS_PATH;
};

export type LocationState =
  | { from?: string; origin?: string }
  | null
  | undefined;

// Where the modal returns to when closing: to the route it was opened from, or to
// "Mes documents" if reached by direct URL.
export const returnPathFrom = (state: unknown): string => {
  const from = (state as LocationState)?.from;
  return typeof from === 'string' && from.startsWith('/') ? from : '/my-documents';
};

// The route the user was on BEFORE entering the search flow. `from` is not
// enough for Résultats' "‹ Retour": after "Modifier ma recherche" the dialog's
// `from` is the results page itself, and following it would trap the user in a
// dialog ↔ results loop with no way back to the Home. The origin is captured
// when the dialog is opened from outside the flow and threaded through
// dialog → results → dialog unchanged.
export const originPathFrom = (state: unknown): string | undefined => {
  const origin = (state as LocationState)?.origin;
  return typeof origin === 'string' &&
    origin.startsWith('/') &&
    !origin.startsWith(SEARCH_PATH)
    ? origin
    : undefined;
};
